import { supabase, getProdSupabaseClient } from './client';
import { translateSystemError, isSystemError, logSystemError } from '@/lib/utils/errorTranslator';
import { getTenantIdFromSession } from './tenantScoping';

export interface Campaign {
  campaign_number: string;
  recall_title: string;
  total_recalls: number;
  skillLevel?: number | null;
  mobile_servicing_eligible?: boolean;
  has_advance_notice_recalls?: boolean; // True if any recall in campaign has advance notice
}

// View-based campaign structure (matches prod.tenant_campaigns_view)
interface ViewCampaign {
  campaign_id: string;
  tenant_id: string;
  campaign_number: string;
  campaign_title: string;
  oem: string | null;
  type: string;
  first_seen_at: string;
  last_seen_at: string;
  has_active_recalls: boolean;
  campaign_metadata: Record<string, unknown> | null;
  campaign_created_at: string;
  campaign_updated_at: string;
  skill_level: number | null;
  mobile_servicing_eligible: boolean;
  campaign_status: string | null;
  status_reason: string | null;
  notes: string | null;
  preference_id: string | null;
  user_id: string | null;
  preference_created_at: string | null;
  preference_updated_at: string | null;
  total_recalls: number; // Total recalls count from materialized view
}

// Helper function to get tenant ID for campaign queries
async function getTenantId(): Promise<string | null> {
  try {
    return await getTenantIdFromSession(supabase);
  } catch (error) {
    if (isSystemError(error)) {
      logSystemError(error, 'getting tenant ID for campaigns');
    }
    return null;
  }
}

// Status is per-user with current-viewer priority (hybrid). Personal overrides
// win for the viewer; teammates' overrides fill in only when the viewer has no
// preference. The Advance Notice MV further pushes any user's "Advance Notice"
// setting to all viewers via the RPC's COALESCE fallback.
//
// Skill Level + Mobile Eligible are tenant-wide last-writer-wins. The most
// recent edit in user_preferences for a campaign is the authoritative value
// for every viewer. Writes still go to per-user rows so user_id + updated_at
// provide a built-in audit trail; the higher-level audit log lives in
// prod.security_audit_logs.
function deduplicateCampaigns(data: ViewCampaign[], userId: string): ViewCampaign[] {
  const byCampaign = new Map<string, ViewCampaign[]>();
  for (const row of data) {
    let bucket = byCampaign.get(row.campaign_number);
    if (!bucket) {
      bucket = [];
      byCampaign.set(row.campaign_number, bucket);
    }
    bucket.push(row);
  }

  const out: ViewCampaign[] = [];
  for (const rows of byCampaign.values()) {
    // Status: legacy current-user > NULL > anyone priority preserved.
    const baseRow =
      rows.find(r => r.user_id === userId) ??
      rows.find(r => r.user_id === null) ??
      rows[0];
    if (!baseRow) continue;

    const skillRow = pickLatestRow(rows, r => r.skill_level != null);
    // preference_id presence distinguishes a real user_preferences row from a
    // global_campaigns row with no preferences at all (the RPC's COALESCE on
    // mobile_servicing_eligible collapses missing to false, so we can't tell
    // "wrote false" from "no preference" by value alone — preference_id can).
    const mobileRow = pickLatestRow(rows, r => r.preference_id != null);

    out.push({
      ...baseRow,
      skill_level: skillRow?.skill_level ?? null,
      mobile_servicing_eligible: mobileRow?.mobile_servicing_eligible ?? false
    });
  }
  return out;
}

function pickLatestRow(
  rows: ViewCampaign[],
  qualifies: (r: ViewCampaign) => boolean
): ViewCampaign | undefined {
  let best: ViewCampaign | undefined;
  let bestTs = -Infinity;
  for (const r of rows) {
    if (!qualifies(r)) continue;
    const ts = r.preference_updated_at ? Date.parse(r.preference_updated_at) : 0;
    if (ts >= bestTs) {
      best = r;
      bestTs = ts;
    }
  }
  return best;
}

// Generic latest-writer reducer for tenant-wide-scoped user_preferences reads
// outside the RPC path (e.g. useViewportMapDataPaginated.ts). Keeps Step 1's
// dedup semantics consistent across hooks.
export interface UserPrefRow {
  entity_id: string;
  preference_value: unknown;
  user_id?: string | null;
  updated_at?: string | null;
}

export function pickLatestPerCampaignField<T>(
  rows: UserPrefRow[],
  extract: (pref: Record<string, unknown>) => T | undefined
): Record<string, T> {
  const latest: Record<string, { value: T; ts: number }> = {};
  for (const row of rows) {
    const pref = row.preference_value;
    if (typeof pref !== 'object' || pref === null) continue;
    const value = extract(pref as Record<string, unknown>);
    if (value === undefined) continue;
    const ts = row.updated_at ? Date.parse(row.updated_at) : 0;
    const existing = latest[row.entity_id];
    if (!existing || ts >= existing.ts) {
      latest[row.entity_id] = { value, ts };
    }
  }
  const out: Record<string, T> = {};
  for (const [k, v] of Object.entries(latest)) out[k] = v.value;
  return out;
}

// Helper: Transform ViewCampaign to Campaign format
function transformToCampaign(viewCampaign: ViewCampaign): Campaign {
  return {
    campaign_number: viewCampaign.campaign_number,
    recall_title: viewCampaign.campaign_title || '',
    total_recalls: viewCampaign.total_recalls || 0,
    skillLevel: viewCampaign.skill_level,
    mobile_servicing_eligible: viewCampaign.mobile_servicing_eligible || false,
    has_advance_notice_recalls: viewCampaign.campaign_status === 'Advance Notice'
  };
}

export const campaignAPI = {
  // Get all campaigns from prod.get_tenant_campaigns RPC function
  // Returns ALL global campaigns with tenant-specific recall counts
  async getCampaigns(): Promise<Campaign[]> {
    try {
      const tenantId = await getTenantId();
      if (!tenantId) {
        console.warn('[campaigns] No tenant ID available, returning empty results');
        return [];
      }

      const prodClient = getProdSupabaseClient();
      // Use RPC function for tenant-specific campaign data
      const { data, error } = await prodClient.rpc('get_tenant_campaigns', {
        p_tenant_id: tenantId
      });

      if (error) {
        if (isSystemError(error)) {
          throw translateSystemError(error, 'getting campaigns from RPC');
        }
        throw error;
      }

      // Transform view data to Campaign format
      const campaigns: Campaign[] = (data || []).map((viewCampaign: ViewCampaign) => ({
        campaign_number: viewCampaign.campaign_number,
        recall_title: viewCampaign.campaign_title || '',
        total_recalls: viewCampaign.total_recalls || 0,
        skillLevel: viewCampaign.skill_level,
        mobile_servicing_eligible: viewCampaign.mobile_servicing_eligible || false,
        has_advance_notice_recalls: viewCampaign.campaign_status === 'Advance Notice'
      }));

      return campaigns;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting campaigns');
        throw translateSystemError(error, 'getting campaigns');
      }
      throw error;
    }
  },

  // Get campaigns with user preferences from prod.get_tenant_campaigns RPC function
  // Returns ALL global campaigns with tenant-specific recall counts and preferences
  async getCampaignsWithPreferences(userId: string): Promise<Campaign[]> {
    try {
      const tenantId = await getTenantId();
      if (!tenantId) {
        return [];
      }

      const prodClient = getProdSupabaseClient();
      // Use RPC function for tenant-specific campaign data
      const { data, error } = await prodClient.rpc('get_tenant_campaigns', {
        p_tenant_id: tenantId
      });

      if (error) {
        if (isSystemError(error)) {
          throw translateSystemError(error, 'getting campaigns with preferences');
        }
        throw error;
      }

      // RPC now returns one row per campaign with proper tenant-specific data
      // Deduplication still useful if there are multiple preference records
      const uniqueData = deduplicateCampaigns(data || [], userId);
      return uniqueData.map(transformToCampaign);
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting campaigns with preferences');
        throw translateSystemError(error, 'getting campaigns with preferences');
      }
      throw error;
    }
  },

  // Sync campaigns from prod.cars_with_safety_recalls_mv to prod.campaigns
  // Extracts unique campaigns from the MV and upserts them to the campaigns table
  async syncCampaigns(): Promise<{
    inserted_count: number;
    updated_count: number;
    total_campaigns: number;
  }> {
    try {
      const tenantId = await getTenantId();
      if (!tenantId) {
        throw new Error('Tenant ID not available');
      }

      const prodClient = getProdSupabaseClient();

      // Call the RPC function to sync campaigns
      const { data, error } = await prodClient.rpc('sync_campaigns_from_mv');

      if (error) {
        if (isSystemError(error)) {
          throw translateSystemError(error, 'syncing campaigns from materialized view');
        }
        throw error;
      }

      // The RPC function returns a table with one row containing the counts
      if (data && data.length > 0) {
        return {
          inserted_count: data[0].inserted_count || 0,
          updated_count: data[0].updated_count || 0,
          total_campaigns: data[0].total_campaigns || 0
        };
      }

      return {
        inserted_count: 0,
        updated_count: 0,
        total_campaigns: 0
      };
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'syncing campaigns');
        throw translateSystemError(error, 'syncing campaigns');
      }
      throw error;
    }
  }
};
